import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AuthState } from '@pharvio/accounts/admin-account/data-access';
import {
  AsyncDataItem,
  AsyncDataItemState,
  callInBatches,
  DashboardCacheLocalDbService,
  IBranchSuperintendentStatusRow,
  ICustomerStats,
  IDashboardTopProduct,
  idleDataItem,
  IExpiringSummary,
  ILowStockSummary,
  IOrderStats,
  IShift,
  IUserCredentialExpiry,
  OfflineModeService,
  TENANT_PERMISSIONS,
  wrapAsAsyncDataItem
} from '@pharvio/core/shared';
import { EMPTY, from } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import { TenantDashboardDataService } from '../../data/tenant-dashboard-data.service';
import {
  LoadCustomerStats,
  LoadDashboard,
  LoadExpiringSoon,
  LoadLowStock,
  LoadShifts,
  LoadOrderStats,
  LoadOutOfStock,
  LoadSuperintendentStatus,
  LoadTopProducts,
  LoadUsersExpiringCredentials,
  SetSelectedBranches
} from './tenant-dashboard.actions';

export interface TenantDashboardStateModel {
  selectedBranchIds: string[];

  orderStats: AsyncDataItem<IOrderStats | null>;
  openShifts: AsyncDataItem<IShift[]>;
  lowStock: AsyncDataItem<ILowStockSummary | null>;
  expiringSoon: AsyncDataItem<IExpiringSummary | null>;
  outOfStock: AsyncDataItem<number>;
  topProducts: AsyncDataItem<IDashboardTopProduct[]>;
  superintendentStatus: AsyncDataItem<IBranchSuperintendentStatusRow[]>;
  usersExpiringCredentials: AsyncDataItem<IUserCredentialExpiry[]>;
  customerStats: AsyncDataItem<ICustomerStats | null>;

  /** When the widgets were last loaded from the server (or cached). */
  lastLoadedAt: number | null;
  /** True when the widgets were hydrated from the device cache (offline). */
  fromCache: boolean;
}

/** The slices persisted per branch set for offline viewing. */
type DashboardCacheSlices = Omit<
  TenantDashboardStateModel,
  'selectedBranchIds' | 'lastLoadedAt' | 'fromCache'
>;

const DEFAULTS: TenantDashboardStateModel = {
  selectedBranchIds: [],
  orderStats: idleDataItem(null),
  openShifts: idleDataItem([]),
  lowStock: idleDataItem(null),
  expiringSoon: idleDataItem(null),
  outOfStock: idleDataItem(0),
  topProducts: idleDataItem([]),
  superintendentStatus: idleDataItem([]),
  usersExpiringCredentials: idleDataItem([]),
  customerStats: idleDataItem(null),
  lastLoadedAt: null,
  fromCache: false
};

@State<TenantDashboardStateModel>({
  name: 'tenantDashboard',
  defaults: DEFAULTS
})
@Injectable()
export class TenantDashboardState {
  private readonly store = inject(Store);
  private readonly svc = inject(TenantDashboardDataService);
  private readonly offlineMode = inject(OfflineModeService);
  private readonly cache = inject(DashboardCacheLocalDbService);

  // ─── Selectors ───

  @Selector() static selectedBranchIds(s: TenantDashboardStateModel): string[] {
    return s.selectedBranchIds;
  }
  @Selector() static orderStats(s: TenantDashboardStateModel) {
    return s.orderStats;
  }
  @Selector() static openShifts(s: TenantDashboardStateModel) {
    return s.openShifts;
  }
  @Selector() static lowStock(s: TenantDashboardStateModel) {
    return s.lowStock;
  }
  @Selector() static expiringSoon(s: TenantDashboardStateModel) {
    return s.expiringSoon;
  }
  @Selector() static outOfStock(s: TenantDashboardStateModel) {
    return s.outOfStock;
  }
  @Selector() static topProducts(s: TenantDashboardStateModel) {
    return s.topProducts;
  }
  @Selector() static superintendentStatus(
    s: TenantDashboardStateModel
  ): AsyncDataItem<IBranchSuperintendentStatusRow[]> {
    return s.superintendentStatus;
  }
  @Selector() static usersExpiringCredentials(s: TenantDashboardStateModel) {
    return s.usersExpiringCredentials;
  }
  @Selector() static customerStats(s: TenantDashboardStateModel) {
    return s.customerStats;
  }
  @Selector() static lastLoadedAt(s: TenantDashboardStateModel): number | null {
    return s.lastLoadedAt;
  }
  @Selector() static fromCache(s: TenantDashboardStateModel): boolean {
    return s.fromCache;
  }

  // ─── Offline cache ───

  /** `null` before a tenant is known — nothing is read or written then. */
  private cacheKey(ctx: StateContext<TenantDashboardStateModel>): string | null {
    const tenantId = this.store.selectSnapshot(AuthState.tenantId);
    return tenantId
      ? DashboardCacheLocalDbService.keyFor(tenantId, ctx.getState().selectedBranchIds)
      : null;
  }

  /** Persists every widget slice for the current branch set (best effort). */
  private persist(ctx: StateContext<TenantDashboardStateModel>): void {
    const tenantId = this.store.selectSnapshot(AuthState.tenantId);
    if (!tenantId) return;
    const {
      selectedBranchIds: _b,
      lastLoadedAt: _l,
      fromCache: _f,
      ...slices
    } = ctx.getState();
    void [_b, _l, _f];
    const key = this.cacheKey(ctx);
    const savedAt = Date.now();
    ctx.patchState({ lastLoadedAt: savedAt, fromCache: false });
    if (!key) return;
    void this.cache
      .put<DashboardCacheSlices>({ key, tenantId, savedAt, data: slices })
      .catch(() => undefined);
  }

  /** Offline: show the last widgets saved for this branch set. */
  private hydrate(ctx: StateContext<TenantDashboardStateModel>) {
    const key = this.cacheKey(ctx);
    if (!key) return EMPTY;
    return from(this.cache.get<DashboardCacheSlices>(key)).pipe(
      tap((record) => {
        if (!record) return;
        ctx.patchState({ ...record.data, lastLoadedAt: record.savedAt, fromCache: true });
      }),
      catchError(() => EMPTY)
    );
  }

  private dispatchInBatches(
    dispatches: unknown[],
    ctx: StateContext<TenantDashboardStateModel>
  ) {
    return callInBatches({
      items: dispatches,
      apiFn: (d) => ctx.dispatch(d),
      minDelay: 50,
      maxDelay: 150
    });
  }

  // ─── Orchestrator ───

  @Action(LoadDashboard)
  loadDashboard(ctx: StateContext<TenantDashboardStateModel>) {
    if (this.offlineMode.isOffline()) return this.hydrate(ctx);
    const perms = this.store.selectSnapshot(AuthState.permissions);
    const can = (p: string) => perms.includes('*') || perms.includes(p);
    const dispatches: unknown[] = [];

    if (can(TENANT_PERMISSIONS.REPORTS.READ)) {
      dispatches.push(new LoadTopProducts());
    }
    if (can(TENANT_PERMISSIONS.ORDERS.READ)) {
      dispatches.push(new LoadOrderStats());
    }
    if (can(TENANT_PERMISSIONS.SHIFTS.READ)) {
      dispatches.push(new LoadShifts());
    }
    if (can(TENANT_PERMISSIONS.INVENTORY.READ)) {
      dispatches.push(
        new LoadLowStock(),
        new LoadExpiringSoon(),
        new LoadOutOfStock()
      );
    }
    if (can(TENANT_PERMISSIONS.BRANCHES.READ)) {
      dispatches.push(new LoadSuperintendentStatus());
    }
    if (can(TENANT_PERMISSIONS.USERS.CREDENTIALS_MANAGE)) {
      dispatches.push(new LoadUsersExpiringCredentials());
    }
    if (can(TENANT_PERMISSIONS.CUSTOMERS.READ)) {
      dispatches.push(new LoadCustomerStats());
    }

    if (dispatches.length === 0) return;
    return this.dispatchInBatches(dispatches, ctx).pipe(
      finalize(() => this.persist(ctx))
    );
  }

  @Action(SetSelectedBranches)
  setSelectedBranches(
    ctx: StateContext<TenantDashboardStateModel>,
    { branchIds }: SetSelectedBranches
  ) {
    ctx.patchState({ selectedBranchIds: branchIds });
    if (this.offlineMode.isOffline()) return this.hydrate(ctx);
    const perms = this.store.selectSnapshot(AuthState.permissions);
    const can = (p: string) => perms.includes('*') || perms.includes(p);
    const dispatches: unknown[] = [];

    if (can(TENANT_PERMISSIONS.REPORTS.READ)) {
      dispatches.push(new LoadTopProducts());
    }
    if (can(TENANT_PERMISSIONS.ORDERS.READ)) {
      dispatches.push(new LoadOrderStats());
    }
    if (can(TENANT_PERMISSIONS.SHIFTS.READ)) {
      dispatches.push(new LoadShifts());
    }
    if (can(TENANT_PERMISSIONS.INVENTORY.READ)) {
      dispatches.push(
        new LoadLowStock(),
        new LoadExpiringSoon(),
        new LoadOutOfStock()
      );
    }

    if (dispatches.length === 0) return;
    return this.dispatchInBatches(dispatches, ctx).pipe(
      finalize(() => this.persist(ctx))
    );
  }

  // ─── Date helpers ───

  private today(): string {
    return new Date().toISOString().split('T')[0];
  }

  private weekAgo(): string {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d.toISOString().split('T')[0];
  }

  private twoWeeksAgo(): string {
    const d = new Date();
    d.setDate(d.getDate() - 13);
    return d.toISOString().split('T')[0];
  }

  private branchParams(
    ctx: StateContext<TenantDashboardStateModel>
  ): HttpParams {
    const ids = ctx.getState().selectedBranchIds;
    let params = new HttpParams();
    if (ids.length === 1) {
      params = params.set('branchId', ids[0]);
    } else if (ids.length > 1) {
      for (const id of ids) {
        params = params.append('branchIds', id);
      }
    }
    return params;
  }

  // ─── Order stats — single 14-day call feeds all order/revenue widgets + trends ───

  @Action(LoadOrderStats)
  loadOrderStats(ctx: StateContext<TenantDashboardStateModel>) {
    if (this.offlineMode.isOffline()) return EMPTY; // cached slices stay as they are
    ctx.patchState({ orderStats: wrapAsAsyncDataItem({ data: null }) });
    const from = `${this.twoWeeksAgo()}T00:00:00Z`;
    const to = `${this.today()}T23:59:59Z`;
    return this.svc
      .getOrderStats(this.branchParams(ctx).set('from', from).set('to', to))
      .pipe(
        tap((stats) =>
          ctx.patchState({
            orderStats: wrapAsAsyncDataItem({
              data: stats,
              state: AsyncDataItemState.LOADED
            })
          })
        ),
        catchError((error) => {
          ctx.patchState({
            orderStats: wrapAsAsyncDataItem({
              data: null,
              state: AsyncDataItemState.ERROR,
              error
            })
          });
          return EMPTY;
        })
      );
  }

  // ─── Shifts (#4) ───

  @Action(LoadShifts)
  loadShifts(ctx: StateContext<TenantDashboardStateModel>) {
    if (this.offlineMode.isOffline()) return EMPTY; // cached slices stay as they are
    ctx.patchState({ openShifts: wrapAsAsyncDataItem({ data: [] }) });
    return this.svc.getShifts(this.branchParams(ctx)).pipe(
      tap((response) =>
        ctx.patchState({
          openShifts: wrapAsAsyncDataItem({
            data: response.data,
            state: AsyncDataItemState.LOADED
          })
        })
      ),
      catchError((error) => {
        ctx.patchState({
          openShifts: wrapAsAsyncDataItem({
            data: [],
            state: AsyncDataItemState.ERROR,
            error
          })
        });
        return EMPTY;
      })
    );
  }

  // ─── Inventory (#5, #6, #7) ───

  @Action(LoadLowStock)
  loadLowStock(ctx: StateContext<TenantDashboardStateModel>) {
    if (this.offlineMode.isOffline()) return EMPTY; // cached slices stay as they are
    ctx.patchState({ lowStock: wrapAsAsyncDataItem({ data: null }) });
    return this.svc.getLowStock(this.branchParams(ctx).set('size', '5')).pipe(
      tap((summary) =>
        ctx.patchState({
          lowStock: wrapAsAsyncDataItem({
            data: summary,
            state: AsyncDataItemState.LOADED
          })
        })
      ),
      catchError((error) => {
        ctx.patchState({
          lowStock: wrapAsAsyncDataItem({
            data: null,
            state: AsyncDataItemState.ERROR,
            error
          })
        });
        return EMPTY;
      })
    );
  }

  @Action(LoadExpiringSoon)
  loadExpiringSoon(ctx: StateContext<TenantDashboardStateModel>) {
    if (this.offlineMode.isOffline()) return EMPTY; // cached slices stay as they are
    ctx.patchState({ expiringSoon: wrapAsAsyncDataItem({ data: null }) });
    return this.svc
      .getExpiringSoon(
        this.branchParams(ctx).set('days', '30').set('size', '5')
      )
      .pipe(
        tap((summary) =>
          ctx.patchState({
            expiringSoon: wrapAsAsyncDataItem({
              data: summary,
              state: AsyncDataItemState.LOADED
            })
          })
        ),
        catchError((error) => {
          ctx.patchState({
            expiringSoon: wrapAsAsyncDataItem({
              data: null,
              state: AsyncDataItemState.ERROR,
              error
            })
          });
          return EMPTY;
        })
      );
  }

  @Action(LoadOutOfStock)
  loadOutOfStock(ctx: StateContext<TenantDashboardStateModel>) {
    if (this.offlineMode.isOffline()) return EMPTY; // cached slices stay as they are
    ctx.patchState({ outOfStock: wrapAsAsyncDataItem({ data: 0 }) });
    return this.svc.getOutOfStockCount(this.branchParams(ctx)).pipe(
      tap((count) =>
        ctx.patchState({
          outOfStock: wrapAsAsyncDataItem({
            data: count,
            state: AsyncDataItemState.LOADED
          })
        })
      ),
      catchError((error) => {
        ctx.patchState({
          outOfStock: wrapAsAsyncDataItem({
            data: 0,
            state: AsyncDataItemState.ERROR,
            error
          })
        });
        return EMPTY;
      })
    );
  }

  // ─── Top products (#10) ───

  @Action(LoadTopProducts)
  loadTopProducts(ctx: StateContext<TenantDashboardStateModel>) {
    if (this.offlineMode.isOffline()) return EMPTY; // cached slices stay as they are
    ctx.patchState({ topProducts: wrapAsAsyncDataItem({ data: [] }) });
    return this.svc
      .getTopProducts(
        this.branchParams(ctx)
          .set('from', this.weekAgo())
          .set('to', this.today())
          .set('size', '5')
      )
      .pipe(
        tap((products) =>
          ctx.patchState({
            topProducts: wrapAsAsyncDataItem({
              data: products,
              state: AsyncDataItemState.LOADED
            })
          })
        ),
        catchError((error) => {
          ctx.patchState({
            topProducts: wrapAsAsyncDataItem({
              data: [],
              state: AsyncDataItemState.ERROR,
              error
            })
          });
          return EMPTY;
        })
      );
  }

  // ─── Compliance (#11, #12, #13) ───

  @Action(LoadSuperintendentStatus)
  loadSuperintendentStatus(ctx: StateContext<TenantDashboardStateModel>) {
    if (this.offlineMode.isOffline()) return EMPTY; // cached slices stay as they are
    ctx.patchState({ superintendentStatus: wrapAsAsyncDataItem({ data: [] }) });
    return this.svc.getSuperintendentStatus().pipe(
      tap((rows) =>
        ctx.patchState({
          superintendentStatus: wrapAsAsyncDataItem({
            data: rows,
            state: AsyncDataItemState.LOADED
          })
        })
      ),
      catchError((error) => {
        ctx.patchState({
          superintendentStatus: wrapAsAsyncDataItem({
            data: [],
            state: AsyncDataItemState.ERROR,
            error
          })
        });
        return EMPTY;
      })
    );
  }

  @Action(LoadUsersExpiringCredentials)
  loadUsersExpiringCredentials(ctx: StateContext<TenantDashboardStateModel>) {
    if (this.offlineMode.isOffline()) return EMPTY; // cached slices stay as they are
    ctx.patchState({
      usersExpiringCredentials: wrapAsAsyncDataItem({ data: [] })
    });
    return this.svc
      .getUsersExpiringCredentials(
        this.branchParams(ctx).set('withinDays', '30')
      )
      .pipe(
        tap((users) =>
          ctx.patchState({
            usersExpiringCredentials: wrapAsAsyncDataItem({
              data: users,
              state: AsyncDataItemState.LOADED
            })
          })
        ),
        catchError((error) => {
          ctx.patchState({
            usersExpiringCredentials: wrapAsAsyncDataItem({
              data: [],
              state: AsyncDataItemState.ERROR,
              error
            })
          });
          return EMPTY;
        })
      );
  }

  // ─── Customers — single 14-day call feeds KPIs + trends + chart ───

  @Action(LoadCustomerStats)
  loadCustomerStats(ctx: StateContext<TenantDashboardStateModel>) {
    if (this.offlineMode.isOffline()) return EMPTY; // cached slices stay as they are
    ctx.patchState({ customerStats: wrapAsAsyncDataItem({ data: null }) });
    const from = this.twoWeeksAgo();
    const to = this.today();
    return this.svc
      .getCustomerStats(this.branchParams(ctx).set('from', from).set('to', to))
      .pipe(
        tap((stats) =>
          ctx.patchState({
            customerStats: wrapAsAsyncDataItem({
              data: stats,
              state: AsyncDataItemState.LOADED
            })
          })
        ),
        catchError((error) => {
          ctx.patchState({
            customerStats: wrapAsAsyncDataItem({
              data: null,
              state: AsyncDataItemState.ERROR,
              error
            })
          });
          return EMPTY;
        })
      );
  }
}
